import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NC, telLink, mailLink, openWhatsApp, defaultWhatsappMessage } from "@/lib/nc";

const details = [
  {
    icon: Phone,
    label: "Mobile",
    value: NC.phoneDisplay,
    href: telLink,
  },
  {
    icon: Mail,
    label: "Email",
    value: NC.email,
    href: mailLink,
  },
  {
    icon: MapPin,
    label: "Address",
    value: NC.address,
    href: null,
  },
];

export const Contact = () => {
  return (
    <section id="contact" className="py-12 sm:py-20">
      <div className="container-nc">
        <div className="overflow-hidden rounded-2xl border border-border bg-gradient-navy shadow-elegant">
          <div className="grid gap-10 p-6 sm:p-10 lg:grid-cols-2 lg:p-12">
            {/* Left: info */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold">
                Get in touch
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-primary-foreground text-balance sm:text-4xl">
                Let&rsquo;s discuss your project
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-primary-foreground/80">
                Reach NC directly through WhatsApp, a call or email. We&rsquo;re based in {NC.city} and ready to help.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button
                  variant="whatsapp"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={() => openWhatsApp(defaultWhatsappMessage)}
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle /> WhatsApp
                </Button>
                <Button asChild variant="cta" size="lg" className="w-full sm:w-auto" data-testid="contact-call-btn">
                  <a href={telLink}><Phone /> Call</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-background text-primary hover:bg-background/90 sm:w-auto"
                  data-testid="contact-email-btn"
                >
                  <a href={mailLink}><Mail /> Email</a>
                </Button>
              </div>
            </motion.div>

            {/* Right: detail cards */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="space-y-4"
            >
              {details.map((d) => {
                const content = (
                  <Card className="flex items-start gap-4 border-0 bg-background/95 p-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <d.icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {d.label}
                      </p>
                      <p className="mt-1 break-words text-base font-semibold text-foreground">
                        {d.value}
                      </p>
                    </div>
                  </Card>
                );
                return d.href ? (
                  <a key={d.label} href={d.href} className="block transition-transform duration-200 hover:-translate-y-0.5">
                    {content}
                  </a>
                ) : (
                  <div key={d.label}>{content}</div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
